import * as Utils from '../../commons/utils/Utils';

/**
 * VEC-3316 — catálogo de UBICACIONES de stock del lado del frontend.
 *
 * Desde esta card el stock puede vivir en dos lugares: un **almacén** del Pañol o una **base**.
 * Guardar repuestos en bases es, por decisión del P.O., la forma predeterminada — es lo que permite
 * que una reserva desde un ticket derive su ubicación por `tickets.movil` → `moviles.base`.
 *
 * Este módulo existe porque el mismo catálogo lo consumen TRES pantallas: el filtro de la grilla de
 * stock (VEC-3313), el destino del formulario de ingreso (VEC-3314) y la ubicación de una reserva
 * (VEC-3316). Sin un lugar común, la lista y su serialización se escribirían tres veces y se
 * separarían en la primera modificación. Mismo criterio que `inventarioMovimientosTipos.js` y
 * `inventarioReservaEstados.js`.
 *
 * ## EL VALOR DE CADA OPCIÓN CARGA SU TIPO: "almacen:6", "base:1161"
 *
 * La ubicación viaja al backend como un PAR (`ubicacionType` + `ubicacionId`) porque la columna es
 * polimórfica, y el backend rechaza el par a medias con 400. Con un solo desplegable que mezcla los
 * dos tipos, el id suelto ya no alcanza: el 6 puede ser el almacén 6 o la base 6, que son cosas
 * distintas.
 *
 * Meter el tipo en el valor es lo que permite que el `select` siga siendo un `select` y que la
 * grilla siga mandando el par completo. La alternativa —dos desplegables, uno de tipo y otro de
 * valor— le pide al usuario que entienda un detalle del modelo de datos para elegir un depósito.
 */

/** Depósitos del Pañol. El mismo endpoint que ya consumían la grilla y el formulario de ingreso. */
const URL_ALMACENES = '/panol2/select/almacenes';

/**
 * Bases. Devuelve solo las que el usuario ve (`getAllActiveSortedFilteredByUser`), que es una
 * propiedad deseable acá y no un efecto colateral: nadie debería poder reservar de una base que no
 * ve. VEC-3316 le sumó los permisos de Inventario a su middleware.
 */
const URL_BASES = '/bases/select';

export const TIPO_ALMACEN = 'almacen';
export const TIPO_BASE = 'base';

/** El separador del valor compuesto. Ni los slugs ni los ids lo contienen. */
const SEP = ':';

/** "almacen" + 6 -> "almacen:6" */
export const serializar = (type, id) => String(type) + SEP + String(id);

/**
 * "almacen:6" -> { type: 'almacen', id: '6' }
 *
 * ⚠️ TOLERA EL FORMATO VIEJO, y no es defensa de más: el filtro de la grilla se persiste en
 * `localStorage` (INVENTARIO_STOCK_FILTROS_STORAGE_KEY), así que cualquier usuario que ya haya
 * elegido un depósito antes de esta card tiene guardado un id PELADO ("6"). Sin este fallback, ese
 * valor se mandaría como `ubicacionType=6` y la grilla se rompería en la primera carga después de
 * deployar — un bug que solo le pasa a quien ya usó la pantalla, y que en local no se ve porque el
 * localStorage está limpio.
 *
 * Un valor sin separador se interpreta como almacén, que es lo único que existía cuando esos
 * valores se guardaron.
 *
 * @returns {{type: string, id: string}|null} null si no hay valor (sin filtro de ubicación)
 */
export const parsear = (valor) => {
	if (valor === null || valor === undefined || valor === '') return null;

	const texto = String(valor);
	const corte = texto.indexOf(SEP);

	if (corte < 0) return { type: TIPO_ALMACEN, id: texto };

	const type = texto.slice(0, corte);
	const id = texto.slice(corte + 1);

	// Un par a medias ("base:") se trata como SIN FILTRO y no como una ubicación con id vacío. El
	// select nunca produce eso —sus opciones siempre traen id—, pero un localStorage corrupto o
	// editado a mano sí, y mandarlo haría que el backend responda 400 `ubicacion_incompleta` en
	// cada carga: la grilla quedaría rota y el usuario no tendría forma de arreglarla salvo
	// reseteando los filtros, sin ninguna pista de por qué.
	if (type === '' || id === '') return null;

	return { type: type, id: id };
};

/**
 * Normaliza la respuesta de un select del backend a `{value, label}`.
 *
 * El label nunca queda `undefined`: MUI le pide `.toLowerCase()` al filtrar y un label indefinido
 * no deja el desplegable vacío, tira el render de la pantalla entera. Es la trampa que documenta
 * `docs/design-system/grid-filter-panel.md` con /transportadoras/select, y el mismo cuidado que ya
 * tenía `InventarioStockGridFiltros`.
 */
const aOpciones = (data, type, sufijo) =>
	(data || [])
		.filter((e) => e && e.value !== undefined && e.value !== null)
		.map((e) => ({
			value: serializar(type, e.value),
			label: (e.label === undefined || e.label === null ? String(e.value) : String(e.label)) + sufijo,
			// Se conservan sueltos para quien necesite el par sin volver a parsear.
			ubicacionType: type,
			ubicacionId: e.value,
		}));

/**
 * Las dos listas juntas, listas para un `select`.
 *
 * ## LOS DOS PEDIDOS VAN EN PARALELO Y SE TOLERAN ENTRE SÍ
 *
 * `Promise.all` cortaría todo si uno falla. Acá cada uno cae a lista vacía por separado, a propósito:
 * un tenant donde `/bases/select` responda 403 —o un rol al que no se le hayan otorgado los permisos
 * nuevos— tiene que seguir viendo los almacenes. Perder la mitad del catálogo es mucho mejor que un
 * desplegable vacío, y muchísimo mejor que un error que tire la pantalla.
 *
 * ## LAS BASES SE ETIQUETAN
 *
 * Una base y un almacén pueden llamarse parecido, y en un desplegable mezclado el usuario no tiene
 * forma de saber qué está eligiendo. El sufijo " (base)" es lo mínimo para desambiguar sin inventar
 * un agrupador que el `select` del panel compartido no soporta.
 *
 * Los almacenes van PRIMERO porque es el orden histórico de esa lista y quien ya usaba la pantalla
 * los encuentra donde estaban.
 *
 * @returns {Promise<Array<{value: string, label: string, ubicacionType: string, ubicacionId: *}>>}
 */
export const cargarUbicaciones = () => {
	const almacenes = Utils.getData(URL_ALMACENES)
		.then((data) => aOpciones(data, TIPO_ALMACEN, ''))
		.catch(() => []);

	const bases = Utils.getData(URL_BASES)
		.then((data) => aOpciones(data, TIPO_BASE, ' (base)'))
		.catch(() => []);

	return Promise.all([almacenes, bases]).then((listas) => listas[0].concat(listas[1]));
};

/**
 * La etiqueta de una ubicación ya elegida, para mostrarla fuera del desplegable (el encabezado del
 * modal de historial, por ejemplo).
 *
 * Devuelve null si las opciones todavía no cargaron, y quien llama decide qué hacer: mostrar el
 * alcance sin nombrarlo es preferible a mostrar el valor crudo, que sería "almacen:6".
 */
export const etiquetaDeUbicacion = (opciones, valor) => {
	if (!valor) return null;

	const elegida = (opciones || []).find((o) => String(o.value) === String(valor));

	return elegida ? elegida.label : null;
};
